import React from "react";
import { motion } from "framer-motion";
import { IoMdBulb } from "react-icons/io";
import { MdOutlineMailOutline } from "react-icons/md";
import { LuCalendarClock } from "react-icons/lu";
import MaButton from "../../../Components/UI/ma-button";
import { detailCopy, screenVariants } from "./plan-config";

const CONTACT_PLAN_IDS = new Set(["premium", "enterprise"]);

function formatMoney(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "$0";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

function getPlanAmount(selected) {
  return (
    Number(
      selected?.raw?.price ??
        String(selected?.price ?? "").replace(/[^0-9.]/g, ""),
    ) || 0
  );
}

function WalletLine({ label, value, strong = false }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-[12px] text-[#6B6B6B] sm:text-[13px]">{label}</span>
      <span
        className={`text-right tabular-nums ${
          strong
            ? "text-[15px] font-bold text-[#272727]"
            : "text-[13px] font-semibold text-[#3A3A3A]"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function StandardWalletDetails({
  selected,
  walletBalance,
  walletLoading,
  upgradeLoading,
  onUpgrade,
  onTopUpWallet,
}) {
  const planAmount = getPlanAmount(selected);
  const safePlanAmount = Number.isFinite(planAmount) ? planAmount : 0;
  const safeWalletBalance = Number.isFinite(Number(walletBalance))
    ? Number(walletBalance)
    : 0;
  const amountToPay = Math.max(0, safePlanAmount - safeWalletBalance);
  const remainingAfterDeduction = Math.max(0, safeWalletBalance - safePlanAmount);
  const hasEnoughBalance = safeWalletBalance >= safePlanAmount;

  return (
    <div className="rounded-[18px] border border-solid border-[#E5E7EB80] bg-[#F8F8F8] px-5 py-5">
      <p className="my-0 text-[14px] font-bold text-[#3A3A3A] sm:text-[15px]">
        Pay with wallet balance
      </p>
      <p className="my-0 mt-2 text-[13px] leading-relaxed text-[#6B6B6B] sm:text-[14px]">
        We'll deduct the Standard plan amount from your available wallet balance.
      </p>

      <div className="mt-5 space-y-3 rounded-[14px] border border-solid border-[#E5E7EB] bg-white px-4 py-4">
        <WalletLine
          label="Wallet Balance"
          value={walletLoading ? "Loading..." : formatMoney(safeWalletBalance)}
        />
        <WalletLine label="Plan Amount" value={formatMoney(safePlanAmount)} />
        <WalletLine
          label="Remaining after deduction"
          value={formatMoney(remainingAfterDeduction)}
        />
        <div className="border-t border-[#EFEFEF] pt-3">
          <WalletLine
            label="Amount to pay"
            value={formatMoney(amountToPay)}
            strong
          />
        </div>
      </div>

      {!walletLoading && !hasEnoughBalance ? (
        <div className="mt-4 rounded-[14px] border border-solid border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
          <p className="my-0 text-[13px] font-semibold text-[#991B1B]">
            Insufficient wallet balance
          </p>
          <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#7F1D1D]">
            You need {formatMoney(amountToPay)} more to upgrade. Current balance:
            {" "}
            {formatMoney(safeWalletBalance)}. Shortfall: {formatMoney(amountToPay)}.
          </p>
        </div>
      ) : null}

      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <MaButton
          fullWidth
          variant="primary"
          title="Upgrade Plan"
          loading={upgradeLoading}
          disabled={walletLoading || upgradeLoading || !hasEnoughBalance}
          onClick={onUpgrade}
          sx={{
            bgcolor: "#2E1834",
            minHeight: "44px",
            borderRadius: "12px",
            fontSize: "13px",
            fontWeight: 600,
            "&:hover": { bgcolor: "#3B2142" },
          }}
        />
        {!hasEnoughBalance ? (
          <MaButton
            fullWidth
            title="Top Up Wallet"
            onClick={onTopUpWallet}
            sx={{
              bgcolor: "#fff",
              color: "#2E1834",
              border: "1px solid #E5E4E2",
              minHeight: "44px",
              borderRadius: "12px",
              fontSize: "13px",
              fontWeight: 600,
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

function ContactUsDetails({ selected }) {
  const subject = encodeURIComponent(`Mamaket ${selected.name} plan inquiry`);
  const href = `mailto:care@mamaket.com?subject=${subject}`;

  return (
    <div className="rounded-[18px] border border-solid border-[#E5E7EB80] bg-[#F8F8F8] px-5 py-5">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] bg-white text-[#2E1834]">
          <MdOutlineMailOutline className="text-[22px]" />
        </div>
        <div>
          <p className="my-0 text-[14px] font-bold text-[#3A3A3A] sm:text-[15px]">
            Contact us to activate {selected.name}
          </p>
          <p className="my-0 mt-2 text-[13px] leading-relaxed text-[#6B6B6B] sm:text-[14px]">
            This plan needs a quick conversation so we can match pricing,
            onboarding, and support to your store size.
          </p>
        </div>
      </div>

      <div className="mt-5 rounded-[14px] border border-solid border-[#E5E7EB] bg-white px-4 py-4">
        <div className="flex items-start gap-3">
          <LuCalendarClock className="mt-0.5 shrink-0 text-[20px] text-[#2E1834]" />
          <div>
            <p className="my-0 text-[13px] font-semibold text-[#272727]">
              What happens next?
            </p>
            <p className="my-0 mt-1 text-[12px] leading-relaxed text-[#6B6B6B]">
              Send us a note and our team will help you pick the right product
              limit, AI support, store management, and launch timeline.
            </p>
          </div>
        </div>
      </div>

      <a
        href={href}
        className="mt-5 inline-flex rounded-[12px] border border-solid border-[#E5E4E2] bg-white px-5 py-3 text-[13px] font-semibold text-[#2E1834] no-underline transition-colors hover:border-primary hover:text-primary"
      >
        Email care@mamaket.com
      </a>
    </div>
  );
}

export function PlanDetailScreen({
  selected,
  onBackToPlans,
  onContinue,
  onViewStandard,
  walletBalance,
  walletLoading,
  isWalletUpgradeFlow,
  upgradeLoading,
  onUpgradeStandard,
  onTopUpWallet,
}) {
  const copy = detailCopy[selected.id] ?? detailCopy.free;
  const isFree = selected.id === "free";
  const isStandard = selected.id === "standard";
  const isContactPlan = CONTACT_PLAN_IDS.has(selected.id);
  const standardAmount = getPlanAmount(selected);
  const standardCanUpgrade =
    !walletLoading && Number(walletBalance || 0) >= standardAmount;
  const isFreeUpgradeFlow = isFree && isWalletUpgradeFlow;
  const isStandardUpgradeFlow = isStandard && isWalletUpgradeFlow;
  const primaryActionLoading =
    upgradeLoading && (isFreeUpgradeFlow || isStandardUpgradeFlow);

  return (
    <motion.div
      key={`details-${selected.id}`}
      variants={screenVariants}
      initial="enter"
      animate="center"
      exit="exit"
    >
      <div className="mb-8">
        <h1 className="my-0 text-[22px] font-[600] tracking-tight text-[#353535] sm:text-[23px]">
          {copy.title}
        </h1>
        <p className="my-0 mt-2 text-[14px] leading-relaxed text-[#458082] sm:text-[14px]">
          {copy.subtitle}
        </p>
      </div>

      <div className="flex flex-col gap-7">
        <div className="flex gap-4 rounded-[18px] border border-solid border-[#E5E7EB80] bg-[#F0FAE5] px-5 py-5">
          <div className="pt-1 text-[28px] leading-none">
            <IoMdBulb className="text-[#FFCC00]" />
          </div>
          <p className="my-0 text-[12px] leading-relaxed text-[#626262] sm:text-[13px]">
            {copy.callout}
          </p>
        </div>

        <div className="rounded-[18px] border border-solid border-[#E5E7EB80] bg-[#F8F8F8] px-5 py-5">
          <p className="my-0 text-[14px] font-bold text-[#3A3A3A] sm:text-[15px]">
            {copy.upgradeTitle}
          </p>
          <p className="my-0 mt-2 text-[13px] leading-relaxed text-[#6B6B6B] sm:text-[14px]">
            {copy.upgradeBody}
          </p>
          {isFree ? (
            <button
              type="button"
              onClick={onViewStandard}
              className="mt-5 cursor-pointer rounded-[12px] border border-solid border-[#E5E4E2] bg-white px-5 py-3 text-[13px] text-[#6B6966] transition-colors hover:border-primary hover:text-primary"
            >
              View standard plan
            </button>
          ) : null}
        </div>

        {isStandardUpgradeFlow ? (
          <StandardWalletDetails
            selected={selected}
            walletBalance={walletBalance}
            walletLoading={walletLoading}
            upgradeLoading={upgradeLoading}
            onUpgrade={onUpgradeStandard}
            onTopUpWallet={onTopUpWallet}
          />
        ) : !isContactPlan ? (
          <div className="rounded-[18px] border border-solid border-[#E5E7EB80] bg-[#F8F8F8] px-5 py-5">
            <p className="my-0 text-[14px] font-bold text-[#3A3A3A] sm:text-[15px]">
              {copy.limitTitle}
            </p>
            <p className="my-0 mt-2 text-[13px] leading-relaxed text-[#6B6B6B] sm:text-[14px]">
              {copy.limitBody}
            </p>
          </div>
        ) : (
          <ContactUsDetails selected={selected} />
        )}
      </div>

      <div className="mt-10 flex items-center gap-4">
        <div>
          <MaButton
            title="Back"
            variant="outlined"
            onClick={onBackToPlans}
            sx={{
              bgcolor: "#F0F0F0",
              color: "#181818",
              minHeight: "48px",
              borderRadius: "16px",
              fontSize: "14px",
              fontWeight: 500,
              boxShadow: "none",
              border: "none",
              "&:hover": { bgcolor: "#E8E8E8", boxShadow: "none" },
            }}
          />
        </div>
        <div className="flex-1">
          <MaButton
            fullWidth
            variant="primary"
            title={
              isStandardUpgradeFlow
                ? "Upgrade Plan"
                : isFreeUpgradeFlow
                  ? "Update Plan"
                : isContactPlan
                ? "Contact us"
                : isFree
                  ? "Continue with free"
                  : `Continue with ${selected.name}`
            }
            onClick={() => {
              if (isStandardUpgradeFlow) {
                if (!standardCanUpgrade || upgradeLoading) return;
                onUpgradeStandard();
                return;
              }
              if (isContactPlan) {
                const subject = encodeURIComponent(
                  `Mamaket ${selected.name} plan inquiry`,
                );
                window.location.href = `mailto:care@mamaket.com?subject=${subject}`;
                return;
              }
              onContinue();
            }}
            sx={{
              bgcolor: "#2E1834",
              minHeight: "48px",
              borderRadius: "16px",
              fontSize: "14px",
              fontWeight: 600,
              "&:hover": { bgcolor: "#3B2142" },
            }}
            loading={primaryActionLoading}
            disabled={
              primaryActionLoading ||
              (isStandardUpgradeFlow && !standardCanUpgrade)
            }
          />
        </div>
      </div>
    </motion.div>
  );
}
